import { useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import TradingChart from "../../../../../Components/PerpsComponents/TradingChart";
import TradingTable from "../../../../../Components/PerpsComponents/TradingTable";
import { TradingTopBar } from "../../../../../Components/PerpsComponents/TradingTopBar";
import TrendingMarquee from "../../../../../Components/PerpsComponents/TrendingMarquee";
import AssetNews from "../../../../../Components/PerpsComponents/AssetNews";
import { getMarketById, formatMarketIndex } from "../../../../../config/markets";

import CockpitOrderPanel from "../../../../../Components/Cockpit/CockpitOrderPanel";
import ScreenFrame from "../../../../../Components/Cockpit/ScreenFrame";
import ChartEndMarker from "../../../../../Components/Cockpit/ChartEndMarker";
import CockpitHeader from "../../../../../Components/Cockpit/CockpitHeader";
import "../../../../../Components/Cockpit/cockpit.css";
import useIsMobile from "../../../../../hooks/useIsMobile";

/**
 * Card Trading — the collectible card markets.
 *
 * Serves the two Pokémon markets only:
 *   /perps/markets/rwa/poke     (POKE index, live on-chain market)
 *   /perps/markets/rwa/psa-10   (PSA-10 index, live on-chain market since
 *                                2026-08-10; previously a /poc preview)
 *
 * This is deliberately the same component tree as `CryptoTrading` — the same
 * marquee, top bar, chart, table, news and order form. The redesign is applied
 * as a *theme*: the page is wrapped in `.ce-cockpit` and `cockpit.css` re-tones
 * those components for the dark treatment. The only component swap is inside
 * the order panel, where the leverage rc-slider becomes the circular risk dial.
 *
 * Theming is scoped: mounting adds `ce-cockpit-active` to <body>, unmounting
 * removes it, so no other market is affected.
 *
 * @param {{marketId?: string}} props - Market to render. Passed explicitly
 *   because these are static routes, so there is no `:marketId` param.
 */
const CardTrading = ({ marketId: marketIdProp }) => {
  const params = useParams();
  const marketId = marketIdProp || params.marketId;
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Refs for synced background scroll (must be before any early returns)
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const chartWrapRef = useRef(null);

  const highlightTradeIndex = searchParams.get("highlightTrade");
  const autoOpenDetail = searchParams.get("openDetail") === "true";

  const market = getMarketById(marketId);

  // Scope the dark theme to this page only.
  useEffect(() => {
    document.body.classList.add("ce-cockpit-active");
    return () => document.body.classList.remove("ce-cockpit-active");
  }, []);

  // When scrolling on background (not on a panel), scroll both panels together
  const handleContainerWheel = useCallback((e) => {
    const leftPanel = leftPanelRef.current;
    const rightPanel = rightPanelRef.current;
    if (!leftPanel || !rightPanel) return;

    const target = e.target;
    if (leftPanel.contains(target) || rightPanel.contains(target)) {
      return;
    }

    e.preventDefault();
    leftPanel.scrollTop += e.deltaY;
    rightPanel.scrollTop += e.deltaY;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("wheel", handleContainerWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleContainerWheel);
  }, [handleContainerWheel]);

  if (!market) {
    return (
      <div className="ce-cockpit w-full min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--t3-ink)" }}>Market Not Found</h2>
          <p style={{ color: "var(--t3-text-2)" }}>The market "{marketId}" does not exist.</p>
        </div>
      </div>
    );
  }

  const marketIndex = market.marketIndex != null ? formatMarketIndex(market.marketIndex) : null;

  const orderPanel = (
    <CockpitOrderPanel tokenIndex={market.tokenIndex} marketIndex={marketIndex} market={market} />
  );

  // The chart, set into the Pokédex front plate.
  const screen = (
    <ScreenFrame>
      <div className="relative" ref={chartWrapRef}>
        <TradingChart chartType={market.chartType} />
        {/* The overlays are sized to the plot, which is far smaller on mobile. */}
        <ChartEndMarker containerRef={chartWrapRef} size={isMobile ? 18 : 30} />
      </div>
    </ScreenFrame>
  );

  // Mobile layout
  if (isMobile) {
    return (
      <div className="ce-cockpit w-full min-h-screen" style={{ paddingBottom: "var(--app-marquee-h, 36px)" }}>
        <CockpitHeader />
        <TrendingMarquee />
        <TradingTopBar tokenIndex={market.tokenIndex} />
        <div className="px-4 py-2">{screen}</div>
        <div className="mobile-trading-form">{orderPanel}</div>
        <div className="mb-5">
          <TradingTable market={marketIndex} highlightTradeIndex={highlightTradeIndex} autoOpenDetail={autoOpenDetail} />
        </div>
        <AssetNews marketId={market.id} />
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      ref={containerRef}
      className="ce-cockpit w-full overflow-hidden flex flex-col"
      // The device fills the viewport minus the docked marquee, and nothing
      // else: unlike the other trading pages there is no app `Header` above it
      // (`MainLayouts` swaps it for `CockpitHeader`, which is a child of this
      // box), so there is no header height to subtract here.
      //
      // `minHeight: 0` is load-bearing. `.ce-cockpit` carries `min-height:
      // 100vh` for the mobile layout, which is a normally-scrolling page; on
      // desktop that min-height beat the height above and pinned the box to a
      // full 100vh, putting its bottom 36px underneath the fixed marquee. The
      // panels below scroll inside it, so their last 36px could never be
      // reached — the reported "cannot scroll below this".
      style={{ height: "calc(100vh - var(--app-marquee-h, 36px))", minHeight: 0 }}
    >
      <CockpitHeader />
      {/* `flex-1 min-h-0` rather than `calc(100% - 74px)`. The header is 82px,
          not 74, and the row adds 20px of its own top padding on top of that,
          so the literal overshot the container by 8px and `overflow-hidden`
          ate the difference. Flex measures the header instead of guessing at
          it, and `min-h-0` is what lets the scroll panels below shrink rather
          than being floored at their content height. */}
      <div className="flex gap-5 w-[92%] max-w-[1600px] mx-auto pt-5 flex-1 min-h-0">
        <div ref={leftPanelRef} className="flex-1 min-w-0 overflow-y-auto pr-1 trading-panel-scroll">
          <TrendingMarquee />
          <TradingTopBar tokenIndex={market.tokenIndex} />
          {screen}
          <TradingTable market={marketIndex} highlightTradeIndex={highlightTradeIndex} autoOpenDetail={autoOpenDetail} />
          <AssetNews marketId={market.id} />
        </div>
        <div ref={rightPanelRef} className="w-[340px] xl:w-[400px] flex-shrink-0 overflow-y-auto pl-1 trading-panel-scroll">
          {orderPanel}
        </div>
      </div>
    </div>
  );
};

export default CardTrading;
